import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, errMsg, origin, vibrate } from "../lib/api";
import { useApp } from "../lib/app";
import { Messages, PersonPicker } from "../components/Review";
import { AiChip, AiLoader, ErrorCard, Header, PersonChip } from "../components/bits";
import { useT } from "../lib/i18n";

const MOODS = [["calm", "😌", "Calm"], ["okay", "🙂", "Okay"], ["overloaded", "😩", "Overloaded"]];

export default function Checkin() {
  const { me } = useApp();
  const nav = useNavigate();
  const qc = useQueryClient();
  const t = useT();
  const [mood, setMood] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState(null);
  const [pick, setPick] = useState(null);
  const [sent, setSent] = useState(null);
  const byId = Object.fromEntries(me.members.map((m) => [m.id, m]));

  const go = async (m) => {
    setMood(m); setBusy(true); setErr(null); setRes(null); setSel(null);
    try { setRes((await api.post("/checkin", { mood: m })).data); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const handoff = async (items) => {
    setBusy(true);
    try {
      const { data } = await api.post("/tasks/handoff", { items, origin: origin() });
      vibrate(); toast.success(t("{n} things off your mind 💜", { n: data.handed_off })); setSent(data); qc.invalidateQueries();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sug = res?.suggestions || [];
  const setSug = (k, p) => setRes({ ...res, suggestions: sug.map((x, j) => (j === k ? { ...x, ...p } : x)) });
  const line = !res ? "" : mood === "calm" ? t("Good to hear. Rest well, {name} 💜", { name: me.me.name }) : mood === "okay" ? t("Okay is okay. Be gentle with yourself tonight, {name} 💜", { name: me.me.name })
    : sug.length ? t("You have {n} open tasks. These {m} could go to others:", { n: res.open, m: sug.length }) : t("You've already handed off everything you can. Rest well tonight 💜");

  return (
    <div>
      <Header title="How's your mind tonight?" kicker={`Hi ${me.me.name}`} />
      <div className="grid grid-cols-3 gap-3">
        {MOODS.map(([k, e, l]) => (
          <button key={k} data-testid={`checkin-${k}`} onClick={() => go(k)} disabled={busy}
            className={`ul-card py-5 flex flex-col items-center gap-1 transition-transform hover:-translate-y-0.5 ${mood === k ? "!border-[#FF6FA3] !border-2" : ""}`}>
            <span className="text-4xl">{e}</span><b>{t(l)}</b>
          </button>
        ))}
      </div>
      <p className="text-sm text-center text-[#6B6680] mt-3">{t("🔒 Only you can see your check-ins.")}</p>
      <div className="mt-4 space-y-3">
        {busy && <AiLoader kind="checkin" />}
        {err && <ErrorCard text={err} onRetry={() => go(mood)} />}
        {sent ? <Messages messages={sent.messages} members={me.members} onDone={() => nav("/")} /> : res && !busy && (
          <>
            <p className="ul-card p-4 font-semibold" data-testid="checkin-line">{line}</p>
            {sug.length > 0 && (
              <div className="space-y-2" data-testid="checkin-suggestions">
                <AiChip />
                {sug.map((x, k) => (
                  <div key={x.task_id} className="ul-card p-3 flex items-center gap-3">
                    {sel && <input type="checkbox" className="w-5 h-5 accent-[#6A5AE0]" checked={sel.includes(k)} onChange={() => setSel(sel.includes(k) ? sel.filter((j) => j !== k) : [...sel, k])} />}
                    <div className="flex-1"><p className="font-bold">{x.title}</p><p className="text-xs text-[#6B6680]">{x.reason}</p></div>
                    <PersonChip m={byId[x.assignee_id]} onClick={() => setPick(k)} testid={`checkin-person-${k}`} />
                  </div>
                ))}
                <div className="flex gap-2">
                  {sel ? <button data-testid="checkin-handoff-chosen" disabled={!sel.length} className="ul-btn ul-primary flex-1" onClick={() => handoff(sel.map((k) => sug[k]))}>{t("Hand off {n}", { n: sel.length })}</button>
                    : <button data-testid="checkin-handoff-all" className="ul-btn ul-grad flex-1" onClick={() => handoff(sug)}>{t("Hand off all {n}", { n: sug.length })}</button>}
                  {!sel && <button data-testid="checkin-choose" className="ul-btn ul-ghost" onClick={() => setSel(sug.map((_, k) => k))}>{t("Choose")}</button>}
                  <button data-testid="checkin-not-tonight" className="ul-btn ul-ghost" onClick={() => nav("/")}>{t("Not tonight")}</button>
                </div>
              </div>
            )}
            {res.often && (
              <div className="rounded-2xl p-4" style={{ background: "#F3F0FF" }} data-testid="checkin-often">
                {t("💜 Feeling this way often? Talk to someone you trust, or call")} <a href="tel:14416" className="font-bold underline">Tele-MANAS 14416</a> {t("(free, 24×7).")}
              </div>
            )}
          </>
        )}
        <Link to="/legal/wellbeing" className="block text-center text-sm text-[#6A5AE0] underline" data-testid="checkin-about">{t("About check-in")}</Link>
      </div>
      <PersonPicker open={pick !== null} onClose={() => setPick(null)} members={me.members.filter((m) => m.id !== me.me.id)} onPick={(id) => setSug(pick, { assignee_id: id, reason: "" })} />
    </div>
  );
}
